// Local translations
export const T = {
  en_GB: {
    // Card
    title: "Calculation Settings",
    description:
      "View the currency and configure the risk-free rate used for calculations in this collection.",
    // Form
    currency: "Currency",
    currencyHint:
      "Currency used for statistics calculations (e.g., expected return, volatility). Set when the collection is created and can't be changed.",
    riskFreeRate: "Risk-Free Rate",
    riskFreeRateHint:
      "Risk-free rate used in statistics calculations (typically government bond yield)",
    bankRate: "Bank Rate",
    bankRateHint:
      "Assumed flat annual interest on a bank savings account, used for the bank-savings comparison in forecast charts",
    // Calculations mode
    calculationsMode: "Calculations Mode",
    calculationsModeHint:
      "Which entity's expected return, volatility and correlation data drive portfolio calculations for this collection.",
    calculationsModeCannotChangeWhileActive:
      "Cannot change calculations mode while the collection is active. Deactivate the collection first.",
    calculationsModeAssetClasses: "Asset Classes",
    calculationsModeProducts: "Products",
    // Requirements
    requirementsTitle: "Collection Settings",
    requiresAssetAllocation: "Requires asset allocation",
    requiresAssetAllocationHint:
      "When on, a product must have an asset-class allocation summing to 100% before it can be activated, and asset-allocation, forecast and expected-value components are available in flows — including the Equity Share Chart and Volatility Chart that enforce a risk model's risk bounds. A flow whose risk model sets risk bounds requires this setting.",
    requiresTimeseries: "Requires timeseries",
    requiresTimeseriesHint:
      "When on, a product must have linked instruments (ticker and dataset) before it can be activated, and the historical-return component is available in flows.",
    requiresIsin: "Require ISIN on products",
    requiresIsinHint:
      "When on, a product must have an ISIN before it can be activated. Only applies in Products mode.",
    // Sustainability
    enableSustainability: "Enable sustainability",
    enableSustainabilityHint: "When enabled, configure criteria on the Sustainability tab.",
    enableMandates: "Enable mandates",
    enableMandatesHint:
      "When enabled, mandate templates and enforcement rules are configured on the Mandates tab, and the mandate components can run in flows.",
    enableMandatesRequiresAssetAllocation:
      "Turn on 'Requires asset allocation' first — the mandate check reads the forecast snapshot.",
    failedToToggleMandates: "Failed to update the mandates setting",
    disableSustainabilityTitle: "Disable sustainability?",
    disableSustainabilityDescription:
      "This removes the sustainability configuration for this collection.",
    disable: "Disable",
    cancel: "Cancel",
    // FS model relation
    financialSituationModel: "Financial situation model",
    financialSituationModelHint:
      "Enables client-status conditions on platforms. Flows using this collection must use the same model.",
    financialSituationModelNone: "None",
    // Risk model (set-once)
    riskModel: "Risk Model",
    riskModelPlaceholder: "Select a risk model",
    riskModelCannotChange: "Risk model cannot be changed once set.",
    riskModelAssignHint:
      "Optional. Required only if you want to create portfolios mapped to risk levels. Once set, the risk model cannot be changed.",
    assignRiskModelTitle: "Assign risk model?",
    assignRiskModelDescription:
      "Once assigned, the risk model cannot be changed. If this collection is active, it will be deactivated until portfolios cover every risk score in the model.",
    assign: "Assign",
    failedToAssignRiskModel: "Failed to assign risk model",
    enforceMinBuyAmounts: "Enforce minimum buy amounts",
    enforceMinBuyAmountsHint:
      "When on, advisors are warned when an allocated amount is below a product's minimum buy, and the flow cannot be completed until every allocation is 0 or at least the minimum.",
    enableDynamicPortfolios: "Enable dynamic portfolios",
    enableDynamicPortfoliosHint:
      "When on, dynamic portfolios can be configured per risk level on the Dynamic Portfolios tab and offered as a portfolio type in flows.",
    enableDynamicPortfoliosRequiresRiskModel:
      "Assign a risk model first — dynamic portfolios are defined per risk level of the collection's risk model.",
    cannotEnableDynamicWhileActive:
      "Cannot enable dynamic portfolios while the collection is active. Deactivate the collection first.",
    allowModelPortfolioRiskDeviation: "Allow choosing a model portfolio from another risk score",
    allowModelPortfolioRiskDeviationHint:
      "Advisors and clients can pick any active model portfolio on the goal's platform. The goal's risk score still drives compliance checks.",
    allowModelPortfolioRiskDeviationRequiresRiskModel: "Requires a risk model",
    allowPartialReplacement: "Allow partial product replacement",
    allowPartialReplacementHint:
      "When a dynamic portfolio has to meet the client's sustainability threshold, replace only the needed share of a product (in whole percentage points) instead of the whole product. The client may hold more products.",
    enableWrapperTypes: "Enable Wrapper Types",
    enableWrapperTypesHint:
      "When on, account wrapper types (e.g. ASK, IPS) can be managed on the Wrapper Types tab and assigned to products in order of preference. Disabling is blocked while any product is tagged to a wrapper type.",
    enableProductTypes: "Use product types",
    enableProductTypesHint:
      "When on, product types can be managed on the Product Types tab, assigned to products, and used as a filter and column in the custom portfolio product picker. Disabling is blocked while any product has a product type.",
    // Validation
    invalidRiskFreeRate: "Please enter a valid risk-free rate",
    invalidBankRate: "Please enter a valid bank rate",
    // Messages
    settingsSaved: "Saved",
    failedToSave: "Failed to save settings",
    // Actions
    save: "Save",
  },
  nb_NO: {
    // Card
    title: "Beregningsinnstillinger",
    description:
      "Se valutaen og konfigurer risikofri rente brukt for beregninger i denne samlingen.",
    // Form
    currency: "Valuta",
    currencyHint:
      "Valuta brukt for statistikkberegninger (f.eks. forventet avkastning, volatilitet). Velges når samlingen opprettes og kan ikke endres.",
    riskFreeRate: "Risikofri rente",
    riskFreeRateHint:
      "Risikofri rente brukt i statistikkberegninger (vanligvis statsobligasjonsrente)",
    bankRate: "Bankrente",
    bankRateHint:
      "Antatt flat årlig rente på en bankkonto, brukt for banksparing-sammenligningen i prognosediagrammer",
    // Calculations mode
    calculationsMode: "Beregningsmodus",
    calculationsModeHint:
      "Hvilken enhets forventede avkastning, volatilitet og korrelasjonsdata som styrer porteføljeberegninger for denne samlingen.",
    calculationsModeCannotChangeWhileActive:
      "Kan ikke endre beregningsmodus mens samlingen er aktiv. Deaktiver samlingen først.",
    calculationsModeAssetClasses: "Aktivaklasser",
    calculationsModeProducts: "Produkter",
    // Requirements
    requirementsTitle: "Samlingsinnstillinger",
    requiresAssetAllocation: "Krever aktivaallokering",
    requiresAssetAllocationHint:
      "Når på må et produkt ha en aktivaklasseallokering som summerer til 100 % før det kan aktiveres, og allokerings-, prognose- og forventet verdi-komponenter er tilgjengelige i flyter — inkludert aksjeandels- og volatilitetsgrafene som håndhever en risikomodells risikogrenser. En flyt med en risikomodell som setter risikogrenser krever denne innstillingen.",
    requiresTimeseries: "Krever tidsserie",
    requiresTimeseriesHint:
      "Når på må et produkt ha tilknyttede instrumenter (ticker og datasett) før det kan aktiveres, og historisk avkastning-komponenten er tilgjengelig i flyter.",
    requiresIsin: "Krev ISIN på produkter",
    requiresIsinHint:
      "Når på må et produkt ha en ISIN før det kan aktiveres. Gjelder bare i Produkter-modus.",
    // Sustainability
    enableSustainability: "Aktiver bærekraft",
    enableSustainabilityHint: "Når aktivert, konfigurer kriterier på Bærekraft-fanen.",
    enableMandates: "Aktiver mandater",
    enableMandatesHint:
      "Når aktivert konfigureres mandatmaler og håndhevingsregler under Mandater, og mandatkomponentene kan kjøre i flyter.",
    enableMandatesRequiresAssetAllocation:
      "Slå på «Krever aktivaallokering» først — mandatkontrollen leser prognoseøyeblikksbildet.",
    failedToToggleMandates: "Kunne ikke oppdatere mandatinnstillingen",
    disableSustainabilityTitle: "Deaktivere bærekraft?",
    disableSustainabilityDescription:
      "Dette fjerner bærekraftskonfigurasjonen for denne samlingen.",
    disable: "Deaktiver",
    cancel: "Avbryt",
    // FS model relation
    financialSituationModel: "Finansiell situasjonsmodell",
    financialSituationModelHint:
      "Aktiverer kundestatus-betingelser på plattformer. Flyter som bruker denne samlingen må bruke samme modell.",
    financialSituationModelNone: "Ingen",
    // Risk model (set-once)
    riskModel: "Risikomodell",
    riskModelPlaceholder: "Velg en risikomodell",
    riskModelCannotChange: "Risikomodellen kan ikke endres når den er satt.",
    riskModelAssignHint:
      "Valgfritt. Påkrevd kun hvis du vil opprette porteføljer koblet til risikonivåer. Når den er satt, kan ikke risikomodellen endres.",
    assignRiskModelTitle: "Tilordne risikomodell?",
    assignRiskModelDescription:
      "Når den er tilordnet, kan ikke risikomodellen endres. Hvis denne samlingen er aktiv, deaktiveres den til porteføljene dekker alle risikoskårene i modellen.",
    assign: "Tilordne",
    failedToAssignRiskModel: "Kunne ikke tilordne risikomodell",
    enforceMinBuyAmounts: "Håndhev minimumsbeløp for kjøp",
    enforceMinBuyAmountsHint:
      "Når på varsles rådgivere når et allokert beløp er under produktets minimumskjøp, og flyten kan ikke fullføres før hver allokering er 0 eller minst minimum.",
    enableDynamicPortfolios: "Aktiver dynamiske porteføljer",
    enableDynamicPortfoliosHint:
      "Når på kan dynamiske porteføljer konfigureres per risikonivå på fanen Dynamiske porteføljer og tilbys som porteføljetype i flyter.",
    enableDynamicPortfoliosRequiresRiskModel:
      "Tilordne en risikomodell først — dynamiske porteføljer defineres per risikonivå i samlingens risikomodell.",
    cannotEnableDynamicWhileActive:
      "Kan ikke aktivere dynamiske porteføljer mens samlingen er aktiv. Deaktiver samlingen først.",
    allowModelPortfolioRiskDeviation: "Tillat valg av modellportefølje fra en annen risikoscore",
    allowModelPortfolioRiskDeviationHint:
      "Rådgivere og kunder kan velge hvilken som helst aktiv modellportefølje på målets plattform. Målets risikoscore styrer fortsatt etterlevelseskontrollene.",
    allowModelPortfolioRiskDeviationRequiresRiskModel: "Krever en risikomodell",
    allowPartialReplacement: "Tillat delvis produkterstatning",
    allowPartialReplacementHint:
      "Når en dynamisk portefølje må nå kundens bærekraftsterskel, erstattes bare den nødvendige andelen av et produkt (i hele prosentpoeng) i stedet for hele produktet. Kunden kan få flere produkter.",
    enableWrapperTypes: "Aktiver innpakningstyper",
    enableWrapperTypesHint:
      "Når på kan innpakningstyper for kontoer (f.eks. ASK, IPS) administreres på fanen Innpakningstyper og tilordnes produkter i prioritert rekkefølge. Deaktivering blokkeres så lenge et produkt er merket med en innpakningstype.",
    enableProductTypes: "Bruk produkttyper",
    enableProductTypesHint:
      "Når på kan produkttyper administreres på fanen Produkttyper, tilordnes produkter og brukes som filter og kolonne i produktvelgeren for egendefinert portefølje. Deaktivering blokkeres så lenge et produkt har en produkttype.",
    // Validation
    invalidRiskFreeRate: "Vennligst skriv inn en gyldig risikofri rente",
    invalidBankRate: "Vennligst skriv inn en gyldig bankrente",
    // Messages
    settingsSaved: "Lagret",
    failedToSave: "Kunne ikke lagre innstillinger",
    // Actions
    save: "Lagre",
  },
} as const;
